import {
  getFlyteSyncStatus,
  syncFlyteTrips,
  type FlyteTripSyncResult,
} from '@/services/api/stockDispatch'

export type FlyteSyncState = FlyteTripSyncResult & {
  status: 'idle' | 'running' | 'completed' | 'failed'
  percent: number
}

const idleState = (): FlyteSyncState => ({
  status: 'idle',
  fetched: 0,
  processed: 0,
  inserted: 0,
  updated: 0,
  linkedToOrders: 0,
  unlinked: 0,
  documentsSaved: 0,
  errors: 0,
  since: '2026-04-01',
  percent: 0,
})

let state: FlyteSyncState = idleState()
const listeners = new Set<(next: FlyteSyncState) => void>()
let pollTimer: ReturnType<typeof setInterval> | null = null
let lastNotifiedStatus: FlyteSyncState['status'] = 'idle'
let sawRunningThisSession = false
const completeListeners = new Set<(next: FlyteSyncState) => void>()

function emit() {
  listeners.forEach((listener) => listener(state))
}

function apply(job: FlyteTripSyncResult): FlyteSyncState {
  const status = job.status ?? 'idle'
  return {
    ...idleState(),
    ...job,
    status,
    percent: job.percent ?? 0,
  }
}

async function pollOnce() {
  try {
    const job = await getFlyteSyncStatus()
    state = apply(job)
    emit()
    if (state.status === 'running') sawRunningThisSession = true
    if (
      sawRunningThisSession &&
      (state.status === 'completed' || state.status === 'failed') &&
      lastNotifiedStatus !== state.status
    ) {
      completeListeners.forEach((listener) => listener(state))
    }
    lastNotifiedStatus = state.status
    if (state.status !== 'running' && pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  } catch {
    // Keep the last known state; the next tick retries.
  }
}

function startPolling() {
  if (pollTimer) return
  void pollOnce()
  pollTimer = setInterval(() => {
    void pollOnce()
  }, 1500)
}

export function getFlyteSyncState() {
  return state
}

export function subscribeFlyteSync(listener: (next: FlyteSyncState) => void) {
  listeners.add(listener)
  listener(state)
  return () => {
    listeners.delete(listener)
  }
}

export function onFlyteSyncSettled(listener: (next: FlyteSyncState) => void) {
  completeListeners.add(listener)
  return () => {
    completeListeners.delete(listener)
  }
}

export function watchFlyteSyncInBackground() {
  void pollOnce().then(() => {
    if (state.status === 'running') startPolling()
  })
}

export async function startFlyteSync(since = '2026-04-01'): Promise<FlyteSyncState> {
  const job = await syncFlyteTrips(since)
  lastNotifiedStatus = 'running'
  sawRunningThisSession = true
  state = apply({ ...job, status: job.status ?? 'running' })
  emit()
  startPolling()
  return state
}

export function flyteSyncCompleteMessage(job: FlyteSyncState) {
  if (job.status === 'failed') {
    return job.errorMessage || 'Flyte sync failed.'
  }
  return (
    `${job.fetched} Flyte trips checked: ${job.inserted} added, ${job.updated} updated, ` +
    `${job.documentsSaved} documents saved, ${job.linkedToOrders} linked to orders, ` +
    `${job.unlinked} awaiting order link` +
    `${job.errors ? `, ${job.errors} errors` : ''}.`
  )
}
